/**
 * El Peso del Bajo — rhythm-02-thumb
 * Construcción por frases (ancla → variación → contraste → cierre), no loops planos.
 */
import type { RhythmSubMission, RhythmTargetNote } from "./rhythmPathPlan";
import { att, makeStep, rst } from "./rhythmPatternBuilders";

const step = (
  partial: Parameters<typeof makeStep>[1]
): RhythmSubMission => makeStep("rhythm-02-thumb", partial);

const p = (pitch: string, duration: number): RhythmTargetNote =>
  att(pitch, duration, "p");

/** Frase de blancas en un solo bajo. */
const blancas = (pitch: string, n: number) =>
  Array.from({ length: n }, () => p(pitch, 2));

/** Frase de negras en un solo bajo. */
const negras = (pitch: string, n: number) =>
  Array.from({ length: n }, () => p(pitch, 1));

export const RHYTHM_02_THUMB: RhythmSubMission[] = [
  // ── Bloque 1 ──
  step({
    id: "thumb-2-1-gravedad",
    title: "Gravedad pura",
    subtitle: "Redondas → blancas · 6ª",
    blockLabel: "Bloque 1 · Anclaje de graves",
    kind: "corto",
    goal: "Peso en la 6ª; luego densificar sin cambiar de cuerda.",
    targetPattern: [
      ...Array.from({ length: 4 }, () => p("E2", 4)),
      ...blancas("E2", 4),
    ],
    instructions: [
      "Apoya el pulgar en la 6ª. i y m descansan.",
      "Primero cuatro redondas (peso puro). Luego cuatro blancas en la misma cuerda.",
      "No cambies de piso: solo cambia la figura.",
    ],
    tip: "El dedo cae; no tires de la cuerda. La densificación prueba que el peso se mantiene.",
    feedbackOk: "¡Peso estable al densificar!",
    feedbackError: "4 redondas y 4 blancas solo en la 6ª.",
  }),
  step({
    id: "thumb-2-2-sotano",
    title: "Bajando al sótano",
    subtitle: "Blancas → negras · 5ª",
    blockLabel: "Bloque 1 · Anclaje de graves",
    kind: "corto",
    goal: "Mismo peso en la 5ª a dos velocidades.",
    targetPattern: [...blancas("A2", 4), ...negras("A2", 8)],
    instructions: [
      "Ahora la 5ª cuerda.",
      "Cuatro blancas para asentar; luego ocho negras sin perder el peso.",
      "Si el sonido se afila, estás tirando: vuelve a dejar caer el dedo.",
    ],
    tip: "Blancas = calma. Negras = mismo ataque, menos aire entre golpes.",
    feedbackOk: "¡Peso estable en la 5ª!",
    feedbackError: "4 blancas + 8 negras en la 5ª.",
  }),
  step({
    id: "thumb-2-3-escalera",
    title: "Escalera de bajos",
    subtitle: "Sube y baja · blancas",
    blockLabel: "Bloque 1 · Anclaje de graves",
    kind: "medio",
    goal: "Navegar los tres bajos en ida y vuelta.",
    targetPattern: [
      // Sube
      p("E2", 2),
      p("A2", 2),
      p("D3", 2),
      p("A2", 2),
      // Baja (espejo)
      p("D3", 2),
      p("A2", 2),
      p("E2", 2),
      p("A2", 2),
      // Cierre: ancla en 6ª + eco
      p("E2", 2),
      p("E2", 2),
      p("D3", 2),
      p("D3", 2),
    ],
    instructions: [
      "Primero sube: 6ª → 5ª → 4ª → 5ª.",
      "Luego baja el espejo: 4ª → 5ª → 6ª → 5ª.",
      "Cierra con dos blancas en 6ª y dos en 4ª (eco del salto).",
    ],
    tip: "La ida y la vuelta no son lo mismo: la vuelta exige reordenar el mapa mental.",
    feedbackOk: "¡Escalera de ida y vuelta!",
    feedbackError: "Sigue la ida, el espejo y el cierre. Una blanca por piso.",
  }),
  step({
    id: "thumb-2-4-resistencia-i",
    title: "Resistencia I",
    subtitle: "Frases A–B–A′ · blancas",
    blockLabel: "Bloque 1 · Anclaje de graves",
    kind: "resistencia",
    isEndurance: true,
    goal: "Pulso grave con forma musical, no un ciclo infinito.",
    targetPattern: [
      // A: vecinos 6–5
      ...Array.from({ length: 4 }, () => [p("E2", 2), p("A2", 2)]).flat(),
      // B: vecinos 5–4
      ...Array.from({ length: 4 }, () => [p("A2", 2), p("D3", 2)]).flat(),
      // A′: salto 6–4 (más espacio)
      ...Array.from({ length: 4 }, () => [p("E2", 2), p("D3", 2)]).flat(),
      // Cierre: bloque por cuerda
      ...blancas("E2", 4),
      ...blancas("A2", 4),
      ...blancas("D3", 4),
    ],
    instructions: [
      "Frase A: alterna 6ª y 5ª. Frase B: 5ª y 4ª. Frase A′: salto 6ª↔4ª.",
      "Cierra con cuatro blancas por cuerda (6ª, luego 5ª, luego 4ª).",
      "Lee la timeline: cada frase pide otra intención, no piloto automático.",
    ],
    tip: "Si te aburre, estás anticipando. Escucha el cambio de frase.",
    feedbackOk: "Forma A–B–A′ sostenida. Bloque 1 listo.",
    feedbackError: "Respeta las frases A, B, A′ y el cierre por bloques.",
  }),

  // ── Bloque 2 ──
  step({
    id: "thumb-2-5-latido",
    title: "Latido profundo",
    subtitle: "Negras · respiro · negras · 6ª",
    blockLabel: "Bloque 2 · Motor de graves",
    kind: "corto",
    goal: "Latido con respiro en el medio.",
    targetPattern: [
      ...negras("E2", 8),
      p("E2", 2),
      p("E2", 2),
      ...negras("E2", 8),
    ],
    instructions: [
      "Ocho negras en la 6ª.",
      "Respiro: dos blancas en la misma cuerda.",
      "Otras ocho negras. El respiro evita el trance repetitivo.",
    ],
    tip: "Usa el respiro para soltar el hombro. Luego retoma el latido limpio.",
    feedbackOk: "¡Latido con forma!",
    feedbackError: "8 negras, 2 blancas, 8 negras — todo en la 6ª.",
  }),
  step({
    id: "thumb-2-6-trote",
    title: "Corcheas graves",
    subtitle: "Corcheas · negra · silencio · 5ª",
    blockLabel: "Bloque 2 · Motor de graves",
    kind: "corto",
    goal: "Corcheas con freno consciente.",
    targetPattern: [
      ...Array.from({ length: 8 }, () => p("A2", 0.5)),
      p("A2", 1),
      rst(1),
      ...Array.from({ length: 8 }, () => p("A2", 0.5)),
      p("A2", 1),
      rst(1),
    ],
    instructions: [
      "Ocho corcheas en la 5ª, luego una negra y un silencio.",
      "Repite el bloque. El silencio también cuenta: no ataques ahí.",
      "Mantén el peso; no conviertas el grupo en rasgueo.",
    ],
    tip: "Corchea grave ≠ rasgueo. Cada ataque sigue siendo un golpe limpio.",
    feedbackOk: "¡Corcheas con freno!",
    feedbackError: "8 corcheas + negra + silencio, dos veces, en la 5ª.",
  }),
  step({
    id: "thumb-2-7-octava",
    title: "Salto de octava",
    subtitle: "Agrupaciones 6ª ↔ 4ª",
    blockLabel: "Bloque 2 · Motor de graves",
    kind: "medio",
    goal: "Mismo salto, distintas agrupaciones.",
    targetPattern: [
      // Pares
      p("E2", 1),
      p("E2", 1),
      p("D3", 1),
      p("D3", 1),
      p("E2", 1),
      p("E2", 1),
      p("D3", 1),
      p("D3", 1),
      // Alterno
      p("E2", 1),
      p("D3", 1),
      p("E2", 1),
      p("D3", 1),
      p("E2", 1),
      p("D3", 1),
      p("E2", 1),
      p("D3", 1),
      // Eco largo
      p("E2", 2),
      p("D3", 2),
      p("E2", 2),
      p("D3", 2),
    ],
    instructions: [
      "Primero en pares: dos negras en 6ª, dos en 4ª (×2).",
      "Luego alterno nota a nota: 6ª–4ª–6ª–4ª…",
      "Cierra con blancas alternando 6ª y 4ª.",
    ],
    tip: "Cambiar la agrupación obliga a replanear el salto. Eso es el ejercicio.",
    feedbackOk: "¡Salto con tres agrupaciones!",
    feedbackError: "Pares, luego alterno, luego blancas. 6ª ↔ 4ª.",
  }),
  step({
    id: "thumb-2-8-resistencia-ii",
    title: "Resistencia II",
    subtitle: "Mapa de frases · negras",
    blockLabel: "Bloque 2 · Motor de graves",
    kind: "resistencia",
    isEndurance: true,
    goal: "Navegar graves con secciones distintas hasta el final.",
    targetPattern: [
      // I: bloques de 4 por cuerda
      ...negras("E2", 8),
      ...negras("A2", 8),
      ...negras("D3", 8),
      // II: caminata 6-5-4-5
      ...Array.from({ length: 4 }, () => [
        p("E2", 1),
        p("A2", 1),
        p("D3", 1),
        p("A2", 1),
      ]).flat(),
      // III: salto 6-4
      ...Array.from({ length: 8 }, () => [p("E2", 1), p("D3", 1)]).flat(),
      // IV: cierre 6-6-5-5-4-4
      ...Array.from({ length: 4 }, () => [
        p("E2", 1),
        p("E2", 1),
        p("A2", 1),
        p("A2", 1),
        p("D3", 1),
        p("D3", 1),
      ]).flat(),
    ],
    instructions: [
      "Sección I: ocho negras por cuerda (6ª, 5ª, 4ª).",
      "Sección II: caminata 6-5-4-5. Sección III: salto 6-4.",
      "Cierre: patrón 6-6-5-5-4-4. Cada sección es un capítulo, no un loop.",
    ],
    tip: "Respira entre secciones. El antebrazo se tensa si aprietas.",
    feedbackOk: "Mapa de graves completo. Misión lista.",
    feedbackError: "Sigue las cuatro secciones. El metrónomo manda.",
  }),
];
